/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  MutationKey,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { GramCore } from "../core.js";
import { jsonWebKeySetsPublishKey } from "../funcs/jsonWebKeySetsPublishKey.js";
import { combineSignals } from "../lib/primitives.js";
import { RequestOptions } from "../lib/sdks.js";
import { JSONWebKey } from "../models/components/jsonwebkey.js";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  PublishJsonWebKeyRequest,
  PublishJsonWebKeySecurity,
} from "../models/operations/publishjsonwebkey.js";
import { unwrapAsync } from "../types/fp.js";
import { useGramContext } from "./_context.js";
import { MutationHookOptions } from "./_types.js";

export type PublishJsonWebKeyMutationVariables = {
  request: PublishJsonWebKeyRequest;
  security?: PublishJsonWebKeySecurity | undefined;
  options?: RequestOptions;
};

export type PublishJsonWebKeyMutationData = JSONWebKey;

export type PublishJsonWebKeyMutationError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * publishKey jsonWebKeySets
 *
 * @remarks
 * Mint and publish a new key from the set's current backing external key. The key is published as pending — visible to verifiers so their caches warm up — unless the set has no active key, in which case it activates immediately. Publishing the same backing key again while its kid is present in the set (including revoked) is refused as a conflict. Reads the public half from the customer's KMS; rate limited per organization. Requires org:admin.
 */
export function usePublishJsonWebKeyMutation(
  options?: MutationHookOptions<
    PublishJsonWebKeyMutationData,
    PublishJsonWebKeyMutationError,
    PublishJsonWebKeyMutationVariables
  >,
): UseMutationResult<
  PublishJsonWebKeyMutationData,
  PublishJsonWebKeyMutationError,
  PublishJsonWebKeyMutationVariables
> {
  const client = useGramContext();
  return useMutation({
    ...buildPublishJsonWebKeyMutation(client, options),
    ...options,
  });
}

export function mutationKeyPublishJsonWebKey(): MutationKey {
  return ["@gram/client", "jsonWebKeySets", "publishKey"];
}

export function buildPublishJsonWebKeyMutation(
  client$: GramCore,
  hookOptions?: RequestOptions,
): {
  mutationKey: MutationKey;
  mutationFn: (
    variables: PublishJsonWebKeyMutationVariables,
  ) => Promise<PublishJsonWebKeyMutationData>;
} {
  return {
    mutationKey: mutationKeyPublishJsonWebKey(),
    mutationFn: function publishJsonWebKeyMutationFn({
      request,
      security,
      options,
    }): Promise<PublishJsonWebKeyMutationData> {
      const mergedOptions = {
        ...hookOptions,
        ...options,
        fetchOptions: {
          ...hookOptions?.fetchOptions,
          ...options?.fetchOptions,
          signal: combineSignals(
            hookOptions?.fetchOptions?.signal,
            options?.fetchOptions?.signal,
          ),
        },
      };
      return unwrapAsync(jsonWebKeySetsPublishKey(
        client$,
        request,
        security,
        mergedOptions,
      ));
    },
  };
}
